// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { XngTabGroupsProps } from "@eisinc/MobiusOverrides/TabGroup/TabGroup";
import { mergeWithCss, xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Select, { SelectPresentationProps } from "@insite/mobius/Select/Select";
import { TabProps } from "@insite/mobius/Tab/Tab";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC, useState } from "react";
import { css } from "styled-components";

type AssumedChildrenType = React.ReactElement<TabProps>[];

export interface TabGroupMobileProps {
    showOnMobile?: BreakpointKey;
    mobileCssOverrides?: TabGroupMobileStyles;
    selectLabel?: string;
}

export interface OwnProps {
    currentTab?: string;
    changeTab: (tabKey: string, event: React.MouseEvent | React.KeyboardEvent) => void;
}

type Props = XngTabGroupsProps & OwnProps & TabGroupMobileProps;

interface TabGroupMobileStyles {
    wrapper?: InjectableCss;
    select?: SelectPresentationProps;
}

const tabGroupMobileStyles: TabGroupMobileStyles = {
    wrapper: {
        css: css`
            display: block;
            width: 100%;

            ${({ showOnMobile }: any) => css`
                ${xScreen("gt", showOnMobile)} {
                    display: none;
                }
            `}

            @media print {
                display: none;
            }
        `,
    },
    select: {
        cssOverrides: {
            formField: css`
                max-width: 292px;
                ${xScreen("lte", "xs")} {
                    max-width: 100%;
                }
            `,
        },
    },
};

const TabGroupMobile: FC<Props> = ({
    showOnMobile = "lg",
    currentTab,
    changeTab,
    mobileCssOverrides,
    selectLabel,
    children,
}) => {
    const [styles] = useState(() => mergeWithCss(tabGroupMobileStyles, mobileCssOverrides));

    const tabs: { key: string; label: string }[] = [];
    React.Children.forEach(children as AssumedChildrenType, thisTab => {
        if (!thisTab) {
            return;
        }
        const { tabKey, hidden } = thisTab.props;
        if (!hidden) {
            tabs.push({ key: tabKey, label: typeof thisTab.props.headline === "string" ? thisTab.props.headline : "" });
        }
    });

    const onTabChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        changeTab(event.currentTarget.value, event as any);
    };

    if (!tabs.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} className="TabGroupWrapperMobile" {...{ showOnMobile }}>
            <Select
                {...styles.select}
                value={currentTab}
                onChange={onTabChangeHandler}
                data-id="tabGroup"
                label={selectLabel}
            >
                {tabs.map(({ key, label }) => (
                    <option key={key} value={key}>
                        {label}
                    </option>
                ))}
            </Select>
        </StyledWrapper>
    );
};

/** @component */
export default TabGroupMobile;
